import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { articles as mockArticles } from '../mock';
import EstimateFab from '../components/EstimateFab';
import ArticleImage from '../components/ArticleImage';

const API = process.env.REACT_APP_BACKEND_URL;

const ESTIMATE_ARTICLES = {
  'contoh-sistem-solar-hybrid-powerx-eah-6-6k': 'hybrid-pxh66k',
  'contoh-aplikasi-solar-tanpa-baterai-1300-watt': 'ongrid-1300w',
  'contoh-aplikasi-solar-hybrid-baterai-standing-1300-watt': 'hybrid-pxh66k',
};

const fmt = (iso) =>
  new Date(iso).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

function Block({ b }) {
  if (b.type === 'p')
    return <p className="text-neutral-700 leading-[1.8] mb-5">{b.text}</p>;
  if (b.type === 'h2')
    return (
      <h2 className="font-display text-2xl md:text-3xl font-bold text-neutral-900 mt-10 mb-4">
        {b.text}
      </h2>
    );
  if (b.type === 'ul')
    return (
      <ul className="space-y-2 mb-6 pl-5">
        {b.items.map((it, i) => (
          <li
            key={i}
            className="relative text-neutral-700 leading-[1.7] before:absolute before:-left-5 before:top-[0.65em] before:w-2 before:h-2 before:bg-px-red before:rounded-full"
          >
            {it}
          </li>
        ))}
      </ul>
    );
  if (b.type === 'image')
    return (
      <figure className="my-8">
        <ArticleImage
          src={b.src}
          alt={b.alt || ''}
          className="rounded-2xl border border-neutral-200 overflow-hidden"
          imgClassName="w-full"
          loading="lazy"
        />
        {b.alt && (
          <figcaption className="text-xs text-neutral-500 mt-2 text-center italic">
            {b.alt}
          </figcaption>
        )}
      </figure>
    );
  if (b.type === 'quote')
    return (
      <blockquote className="border-l-4 border-px-red bg-red-50/60 rounded-r-xl px-6 py-5 my-8 text-neutral-800 italic leading-[1.7]">
        {b.text}
      </blockquote>
    );
  if (b.type === 'cta')
    return (
      <div className="my-10">
        <Button
          asChild
          className="bg-px-red hover:bg-red-700 text-white rounded-full px-6 py-3.5 text-sm font-semibold whitespace-normal h-auto text-center"
        >
          <Link to={b.to} data-testid="article-cta">
            {b.text} <ArrowUpRight className="w-4 h-4 ml-1" />
          </Link>
        </Button>
      </div>
    );
  return null;
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [articles, setArticles] = useState(mockArticles);
  const [loaded, setLoaded] = useState(false);
  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [articles, slug]
  );
  const related = useMemo(
    () => articles.filter((a) => a.slug !== slug).slice(0, 3),
    [articles, slug]
  );

  useEffect(() => {
    axios
      .get(`${API}/api/site/articles`)
      .then((r) => {
        if (r.data?.length) setArticles(r.data);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!article) {
    if (!loaded)
      return (
        <div className="pt-[68px] md:pt-[100px] min-h-[60vh] flex items-center justify-center">
          <p className="text-sm text-neutral-500">Memuat artikel...</p>
        </div>
      );
    return <Navigate to="/articles" replace />;
  }

  return (
    <div data-testid="page-article-detail" className="pt-[68px] md:pt-[100px] bg-white">
      {/* Hero */}
      <section
        className="relative bg-neutral-950 text-white overflow-hidden"
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${article.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/50 via-neutral-950/80 to-neutral-950" />
        <span
          className="absolute bottom-3 right-3 z-10 text-[10px] font-bold tracking-[0.2em] text-white/70 bg-black/35 px-1.5 py-0.5 rounded pointer-events-none select-none"
          data-testid="article-image-watermark"
        >
          powerx
        </span>
        <div className="relative max-w-[900px] mx-auto px-6 lg:px-12 py-16 md:py-24">
          <nav className="text-xs text-neutral-300 flex items-center gap-2 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to="/articles" className="hover:text-white">Artikel</Link>
            <span>/</span>
            <span className="text-white truncate max-w-[200px]">{article.title}</span>
          </nav>
          <span className="inline-block bg-px-red text-white text-[10px] font-bold tracking-[0.25em] px-3 py-1 rounded-full mb-5">
            {article.tag}
          </span>
          <h1 className="font-display text-3xl md:text-5xl font-bold leading-[1.1]">
            {article.title}
          </h1>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[900px] mx-auto px-6 lg:px-12 py-14">
        <Link
          to="/articles"
          className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 mb-8"
          data-testid="article-back"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali ke artikel
        </Link>
        <article className="text-[17px]">
          <p className="text-xl text-neutral-900 font-medium leading-[1.6] mb-8 pb-8 border-b border-neutral-200">
            {article.excerpt}
          </p>
          {article.body.map((b, i) => (
            <Fragment key={i}>
              <Block b={b} />
              {(ESTIMATE_ARTICLES[slug] || article.estimate) && b.type === 'image' && (
                <div
                  className="flex justify-end -mt-4 mb-8"
                  data-testid="article-estimate-slot"
                >
                  <EstimateFab
                    to={`/estimasi-biaya?s=${ESTIMATE_ARTICLES[slug] || `article-${slug}`}`}
                  />
                </div>
              )}
            </Fragment>
          ))}
          {(ESTIMATE_ARTICLES[slug] || article.estimate) &&
            !article.body.some((b) => b.type === 'image') && (
              <div className="flex justify-end mb-8" data-testid="article-estimate-slot">
                <EstimateFab
                  to={`/estimasi-biaya?s=${ESTIMATE_ARTICLES[slug] || `article-${slug}`}`}
                />
              </div>
            )}
        </article>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-neutral-50 border-t border-neutral-200">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14">
            <p className="text-[11px] tracking-[0.25em] text-px-red font-semibold mb-3">
              ARTIKEL LAIN
            </p>
            <h2 className="font-display text-3xl font-bold text-neutral-900 mb-8">
              Lanjutkan membaca
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/articles/${a.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-neutral-200 hover:shadow-lg hover:-translate-y-1 transition-all"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <ArticleImage
                      src={a.image}
                      alt={a.title}
                      className="w-full h-full"
                      imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-[10px] tracking-[0.25em] text-px-red font-bold">
                      {a.tag}
                    </span>
                    <h3 className="font-display text-lg font-bold text-neutral-900 leading-snug mt-2 line-clamp-2 group-hover:text-px-red transition-colors">
                      {a.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
